import { useEffect, useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { RadioSegments } from "@/components/ui/radio-segments";
import { trainingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import { formatNumber } from "./lib/format";
import { failureKind, feedbackMuscles } from "./lib/sessionSets";

// backend/training/models.py — TrainingPumpEnum / TrainingWorkloadEnum.
const PUMP = [
  { value: "low", label: "Low" },
  { value: "moderate", label: "Moderate" },
  { value: "amazing", label: "Amazing" },
];
const WORKLOAD = [
  { value: "easy", label: "Easy" },
  { value: "just_right", label: "Just right" },
  { value: "pushed_limits", label: "Pushed my limits" },
  { value: "too_much", label: "Too much" },
];

/** The form's answers from feedback already given (an "all" entry fills every muscle). */
function answersFrom(feedback, muscles) {
  const all = feedback?.all ?? {};
  const out = {};
  for (const { muscle } of muscles) {
    const own = feedback?.[muscle] ?? {};
    const pump = own.pump ?? all.pump;
    const workload = own.workload ?? all.workload;
    if (pump || workload) out[muscle] = { ...(pump ? { pump } : {}), ...(workload ? { workload } : {}) };
  }
  return out;
}

/** The answered muscles as the API's feedback: `{muscle: {pump?, workload?}}`. */
function feedbackOf(answers, muscles) {
  const out = {};
  for (const { muscle } of muscles) {
    const answer = answers[muscle];
    if (answer?.pump || answer?.workload) out[muscle] = { ...answer };
  }
  return out;
}

const setsWord = (n) => `${formatNumber(n)} ${Number(n) === 1 ? "set" : "sets"}`;

/**
 * Finish a session (walkthrough 7) — or, for one already finished, change
 * its feedback (History). One short form for the muscles with ≥ 2
 * fractional sets in it (lib/sessionSets.js): the pump (low / moderate /
 * amazing) and the workload (easy / just right / pushed my limits / too
 * much), with an *All muscles* row that answers every one at once. Every
 * answer is optional: a muscle left out holds its weekly sets (D12).
 *
 * *Finish* sends what was answered; *End without feedback* is the same
 * call with none; *Discard* deletes the session and its sets after an
 * in-app confirm. One request at a time.
 *
 * Contract (shared with History):
 * - `session`: a TrainingSession (its `sets` decide the muscles; pass the
 *   session view's queued sets in too, so they count).
 * - `onFinished(session)`: the server's copy after the finish.
 * - `onDiscarded()`: it's gone (also when it already was — a 404). The
 *   screen's part: only while the form is still mounted.
 * - `onDiscardConfirmed(sessionId)` (optional): the same news for what this
 *   device keeps for the session (its queued sets). Runs once mono has
 *   discarded it, before `onDiscarded` — and even when the person has left
 *   meanwhile and the form is gone, so it must not touch the screen.
 * - `onDiscardAsked(sessionId)` (optional): the discard is being sent —
 *   before any answer, which a reload or a closed tab can lose. Mark what
 *   the device keeps for it, so a later look still knows it was asked for.
 * - `onDiscardRefused(sessionId)` (optional): mono answered and kept it (a
 *   refusal, a throttle, signed out) — even after the form is gone. Not
 *   for no answer (offline, a 5xx): that may have been a yes.
 * - `onCancel()` (optional): a way out drawn with the other buttons —
 *   off, like them, while a request runs, so a finish in flight can't be
 *   walked away from and its answer lost. `cancelLabel` names it.
 * - `fromHistory`: opened on History's session page, for a session that
 *   closed itself (D18). It already ended without feedback, so there's no
 *   *End without feedback* (it would do nothing), and no summary is
 *   promised (the page is one).
 */
function FinishForm({
  session,
  exercises,
  onFinished,
  onDiscarded,
  onDiscardConfirmed,
  onDiscardAsked,
  onDiscardRefused,
  onCancel,
  cancelLabel = "Cancel",
  fromHistory = false,
}) {
  const id = useId();
  const byKey = useMemo(() => new Map((exercises ?? []).map((e) => [e.key, e])), [exercises]);
  const muscles = useMemo(() => feedbackMuscles(session.sets, byKey), [session.sets, byKey]);
  const editing = session.session_state === "finished";
  const [answers, setAnswers] = useState(() => answersFrom(session.feedback, muscles));
  const [busy, setBusy] = useState(null); // "finish" | "end" | "discard"
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [discardError, setDiscardError] = useState(null);
  const busyRef = useRef(false);
  const mounted = useRef(true);
  const heading = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The button that opened the form (the session's Finish…, History's Add
  // feedback…) went away with the click: start a keyboard or screen-reader
  // user at the form's own heading, not on <body>.
  useEffect(() => {
    heading.current?.focus();
  }, []);

  const answered = feedbackOf(answers, muscles);
  const answeredCount = Object.keys(answered).length;
  const setCount = session.sets?.length ?? 0;

  const answer = (muscle, field, value) =>
    setAnswers((current) => ({ ...current, [muscle]: { ...current[muscle], [field]: value } }));
  const answerAll = (field, value) =>
    setAnswers((current) => {
      const next = { ...current };
      for (const { muscle } of muscles) next[muscle] = { ...next[muscle], [field]: value };
      return next;
    });
  const clear = (muscle) =>
    setAnswers((current) => {
      const next = { ...current };
      delete next[muscle];
      return next;
    });
  const common = (field) => {
    const values = new Set(muscles.map(({ muscle }) => answers[muscle]?.[field]));
    return values.size === 1 ? [...values][0] : undefined;
  };

  const finish = async (withFeedback) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(withFeedback ? "finish" : "end");
    setError(null);
    let body = {};
    if (editing) body = { feedback: answered };
    else if (withFeedback && answeredCount > 0) body = { feedback: answered };
    try {
      const { data } = await trainingApi.sessions.finish(session.id, body);
      if (!mounted.current) return;
      onFinished(data);
    } catch (err) {
      if (mounted.current) {
        setError(apiErrorMessage(err, editing ? "Couldn't save the feedback." : "Couldn't finish the session."));
      }
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(null);
    }
  };

  const discard = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy("discard");
    setDiscardError(null);
    let gone; // mono says it's gone (or already was)
    onDiscardAsked?.(session.id);
    try {
      await trainingApi.sessions.discard(session.id);
      gone = true;
    } catch (err) {
      const kind = failureKind(err);
      gone = kind === "gone"; // already gone: as asked
      if (!gone && kind !== "offline") onDiscardRefused?.(session.id); // kept: an answer
      if (!gone && mounted.current) setDiscardError(apiErrorMessage(err, "Couldn't discard the session."));
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(null);
    }
    if (!gone) return;
    // What the device kept for it goes whether or not the person is still
    // here; the screen's part only if they are.
    onDiscardConfirmed?.(session.id);
    if (!mounted.current) return;
    setConfirming(false);
    onDiscarded();
  };

  const off = busy !== null;
  return (
    <section aria-labelledby={`${id}-title`} className="flex flex-col gap-4 rounded-lg border bg-card p-4 md:p-6">
      <div>
        <h2
          ref={heading}
          id={`${id}-title`}
          tabIndex={-1}
          className="m-0 rounded-sm font-display text-xl font-semibold outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
        >
          {editing ? "Feedback" : "Finish the session"}
        </h2>
        <p className="mt-1 mb-0 text-sm text-muted-foreground">
          {muscles.length > 0
            ? "For each muscle with 2 or more sets: the pump (how full it felt) and the workload (how hard the sets were). Answer what you can — a muscle you leave out keeps its weekly sets as they are."
            : "No muscle got 2 or more sets, so there's nothing to rate."}
        </p>
      </div>

      {muscles.length > 1 && (
        <fieldset className="flex min-w-0 flex-col gap-3 rounded-md border border-dashed p-3" disabled={off}>
          <legend className="px-1 text-sm font-semibold">All muscles at once</legend>
          <RadioSegments legend="Pump" options={PUMP} value={common("pump")} onChange={(v) => answerAll("pump", v)} />
          {/* phoneGrid: four long labels wrapped the joined row at 390 px
              and broke its borders; two to a row there instead (W5). */}
          <RadioSegments
            legend="Workload"
            options={WORKLOAD}
            value={common("workload")}
            onChange={(v) => answerAll("workload", v)}
            phoneGrid
          />
        </fieldset>
      )}

      {muscles.map(({ muscle, label, sets }) => (
        <div key={muscle} className="border-t pt-3">
        <fieldset className="flex min-w-0 flex-col gap-3" disabled={off}>
          {/* The space sits outside the span: inside it, the name read "Chest· 3 sets". */}
          <legend className="mb-3 text-sm font-semibold">
            {label}{" "}
            <span className="font-normal text-muted-foreground">· {setsWord(sets)}</span>
          </legend>
          <RadioSegments
            legend="Pump"
            options={PUMP}
            value={answers[muscle]?.pump}
            onChange={(v) => answer(muscle, "pump", v)}
          />
          <div className="flex flex-wrap items-end gap-x-2 gap-y-1">
            <RadioSegments
              legend="Workload"
              options={WORKLOAD}
              value={answers[muscle]?.workload}
              onChange={(v) => answer(muscle, "workload", v)}
              phoneGrid
              className="w-full sm:w-auto"
            />
            {answers[muscle] && (
              <Button
                type="button"
                variant="ghost"
                className="h-11 px-3 text-muted-foreground md:h-9"
                aria-label={`Clear ${label.toLowerCase()} answers`}
                onClick={() => clear(muscle)}
              >
                Clear
              </Button>
            )}
          </div>
        </fieldset>
        </div>
      ))}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row sm:flex-wrap">
        <Button type="button" className="h-11 md:h-10" disabled={off} onClick={() => finish(true)}>
          {(busy === "finish") && <Loader2 className="animate-spin" aria-hidden="true" />}
          {editing
            ? busy === "finish" ? "Saving…" : "Save feedback"
            : busy === "finish" ? "Finishing…" : answeredCount > 0 ? "Finish with feedback" : "Finish"}
        </Button>
        {!editing && !fromHistory && muscles.length > 0 && (
          <Button type="button" variant="outline" className="h-11 md:h-10" disabled={off} onClick={() => finish(false)}>
            {busy === "end" && <Loader2 className="animate-spin" aria-hidden="true" />}
            {busy === "end" ? "Ending…" : "End without feedback"}
          </Button>
        )}
        {onCancel && (
          <Button type="button" variant="ghost" className="h-11 md:h-10" disabled={off} onClick={onCancel}>
            {cancelLabel}
          </Button>
        )}
        {session.can_discard && (
          <Button
            type="button"
            variant="outline"
            className="h-11 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive sm:ms-auto md:h-10"
            disabled={off}
            onClick={() => {
              setDiscardError(null);
              setConfirming(true);
            }}
          >
            Discard session…
          </Button>
        )}
      </div>
      <p className="m-0 text-xs text-muted-foreground">
        {editing
          ? "Saving replaces this session's feedback; the next plan reads it."
          : fromHistory
            ? "Finish saves your answers and counts it as finished at its last activity, so they count toward your next plans."
            : muscles.length > 0
              ? "Finish saves your answers and closes the session; End without feedback closes it with none. Either way you'll see a summary."
              : "Finish closes the session; you'll see a summary."}
        {session.can_discard ? " Discard deletes it and its sets." : ""}
      </p>

      <AlertDialog open={confirming} onOpenChange={(open) => busy !== "discard" && setConfirming(open)}>
        {/* data-training: portalled out of the layout's wrapper, so it
            carries the scope that keeps its muted text AA (tailwind.css). */}
        <AlertDialogContent
          data-training=""
          onEscapeKeyDown={(event) => busy === "discard" && event.preventDefault()}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this session?</AlertDialogTitle>
            <AlertDialogDescription>
              {setCount > 0
                ? `Its ${setCount} logged ${setCount === 1 ? "set goes" : "sets go"} with it, and nothing from it counts toward your plans.`
                : "Nothing from it counts toward your plans."}{" "}
              This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {discardError && (
            <p role="alert" className="m-0 text-sm text-destructive">
              {discardError}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11 md:h-9" disabled={busy === "discard"}>
              Keep it
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              className="h-11 md:h-9"
              disabled={busy === "discard"}
              onClick={(event) => {
                event.preventDefault();
                discard();
              }}
            >
              {busy === "discard" && <Loader2 className="animate-spin" aria-hidden="true" />}
              {busy === "discard" ? "Discarding…" : "Discard session"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

FinishForm.propTypes = {
  /** The TrainingSession — open (finish it) or finished (change its feedback). */
  session: PropTypes.shape({
    id: PropTypes.number.isRequired,
    session_state: PropTypes.string,
    sets: PropTypes.arrayOf(PropTypes.object),
    feedback: PropTypes.object,
    can_discard: PropTypes.bool,
  }).isRequired,
  /** The catalog — its muscle weights decide which muscles are asked about. */
  exercises: PropTypes.arrayOf(PropTypes.object).isRequired,
  onFinished: PropTypes.func.isRequired,
  onDiscarded: PropTypes.func.isRequired,
  /** Mono discarded it (`sessionId`): forget what the device kept for it. Runs even after unmount. */
  onDiscardConfirmed: PropTypes.func,
  onDiscardAsked: PropTypes.func,
  onDiscardRefused: PropTypes.func,
  /** Optional way out, off while a request runs. */
  onCancel: PropTypes.func,
  cancelLabel: PropTypes.string,
  /** On History's session page: no End without feedback, no summary promised. */
  fromHistory: PropTypes.bool,
};

export default FinishForm;
